import { useEffect, useRef, useState } from 'react';
import { GridColumnVisibilityModel } from '@mui/x-data-grid-premium';

interface FiltersWithSites {
  sites?: Array<{ label?: string; id?: string }>;
}

/**
 * Configuration for additional columns (non-site columns)
 * Maps column names to their visibility boolean
 */
type AdditionalColumnConfig = Record<string, boolean>;

/**
 * Custom hook to manage column visibility with site column logic and optional additional columns.
 * Shows the site/siteName column only when multiple sites are selected.
 * Supports managing additional columns with their own visibility conditions.
 *
 * @param filters - The filter object containing sites array
 * @param columnName - The name of the site column ('siteName' or 'site'). Defaults to 'siteName'
 * @param additionalColumns - Optional object mapping additional column names to their visibility state
 * @returns Object with columnVisibilityModel and onColumnVisibilityModelChange handler
 *
 * @example
 * // Single site column only
 * useSiteColumnVisibility(filters, 'siteName')
 *
 * @example
 * // Site column + additional columns with fixed visibility
 * useSiteColumnVisibility(filters, 'siteName', { actions: isWriter, employeePosId: isCfcOrg })
 */
function useSiteColumnVisibility(
  filters?: FiltersWithSites,
  columnName?: string,
  additionalColumns?: AdditionalColumnConfig
) {
  const resolvedColumnName = columnName ?? 'siteName';
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    [resolvedColumnName]: true,
    ...additionalColumns,
  });

  // Track the previous column name so we can remove the stale key from the model
  // if columnName ever changes between renders.
  const prevColumnNameRef = useRef<string>(resolvedColumnName);

  // Serialize additionalColumns to a stable primitive to prevent infinite re-render loops.
  // Callers often pass inline object literals (e.g. { employeePosId: isCfcOrg }) which
  // produce a new reference on every render; using the object directly as a dep would
  // cause the effect to fire → state update → re-render → new reference → repeat.
  const additionalColumnsKey = additionalColumns === undefined ? undefined : JSON.stringify(additionalColumns);

  useEffect(() => {
    if (filters) {
      const { sites } = filters;
      const prevName = prevColumnNameRef.current;
      prevColumnNameRef.current = resolvedColumnName;
      setColumnVisibilityModel((prev) => {
        const next = { ...prev };
        if (prevName !== resolvedColumnName) {
          delete next[prevName];
        }
        return {
          ...next,
          [resolvedColumnName]: (sites?.length ?? 0) > 1,
          ...additionalColumns,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, resolvedColumnName, additionalColumnsKey]);

  return {
    columnVisibilityModel,
    onColumnVisibilityModelChange: setColumnVisibilityModel,
  };
}

export default useSiteColumnVisibility;
